import { useEffect, useMemo, useState } from "react";
import { fetchAgents, assignableAgents, type Agent } from "../services/agentsService";

// ===========================================================================
// Responsable de un lead o un negocio: uno de los vendedores activos del CRM
// (Configuración → Agentes → Humanos). La lista se pide una vez por pantalla.
// ===========================================================================

/** Vendedores activos + todos los agentes (para mostrar el nombre de uno que ya no está activo). */
export function useOwnerAgents(): { assignable: Agent[]; nameOf: (id: string | null | undefined) => string | null } {
  const [agents, setAgents] = useState<Agent[]>([]);
  useEffect(() => {
    let alive = true;
    fetchAgents({ limit: 300 })
      .then((page) => alive && setAgents(page.data))
      .catch(() => {
        /* sin lista: el selector solo ofrece «Sin responsable» */
      });
    return () => {
      alive = false;
    };
  }, []);
  // Estables entre renders: quien los use en un useMemo no recalcula de más.
  return useMemo(
    () => ({
      assignable: assignableAgents(agents),
      nameOf: (id: string | null | undefined) => (id ? agents.find((a) => a.id === id)?.name ?? null : null),
    }),
    [agents],
  );
}

export function OwnerSelect({
  value,
  agents,
  nameOf,
  onChange,
  disabled = false,
  isDarkMode,
  className = "",
}: {
  value: string | null | undefined;
  agents: Agent[];
  nameOf: (id: string | null | undefined) => string | null;
  onChange: (ownerId: string | null) => void;
  disabled?: boolean;
  isDarkMode: boolean;
  className?: string;
}) {
  const current = value || "";
  // Un responsable que ya no es elegible (inactivo, borrado o de otro origen) se
  // sigue mostrando: cambiarlo es decisión de quien edita, no del selector.
  const orphan = current && !agents.some((a) => a.id === current);
  return (
    <select
      aria-label="Responsable"
      value={current}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className={`rounded-md border px-2 py-1 text-xs outline-none ${
        isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
      } ${className}`}
    >
      <option value="">Sin responsable</option>
      {orphan && <option value={current}>{nameOf(current) ?? "Responsable no disponible"}</option>}
      {agents.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </select>
  );
}
